/**
 * src/components/categories/CategoryFormModal.tsx
 * Ultra-Premium Apple Sheet Modal for Creating & Editing Categories
 */

import React, { useState, useEffect } from 'react';
import { GlassModal } from '../ui/GlassModal';
import { type CategoryItem } from '../../services/api';
import { CATEGORY_ICONS, APPLE_COLORS, renderCategoryIcon } from '../../utils/categoryIcons';
import { Sparkles, Check } from 'lucide-react';

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; icon: string; color: string }) => Promise<void>;
  initialCategory?: CategoryItem | null;
}

export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialCategory
}) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('Tag');
  const [color, setColor] = useState('#0a84ff');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isEditing = Boolean(initialCategory);

  useEffect(() => {
    if (initialCategory) {
      setName(initialCategory.name);
      setIcon(initialCategory.icon || 'Tag');
      setColor(initialCategory.color || '#0a84ff');
    } else {
      setName('');
      setIcon('Tag');
      setColor('#0a84ff');
    }
    setErrorMessage('');
  }, [initialCategory, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!name.trim()) {
      setErrorMessage('Please enter a category name');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({
        name: name.trim(),
        icon,
        color
      });
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Category' : 'New Category Master'}
      maxWidth="500px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {errorMessage && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '14px',
              background: 'rgba(255, 69, 58, 0.12)',
              border: '1px solid rgba(255, 69, 58, 0.3)',
              color: 'var(--ios-red)',
              fontSize: '0.84rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Live Apple Squircle Preview Card */}
        <div
          style={{
            padding: '20px',
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: 'var(--border-hairline)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08)'
          }}
        >
          <div
            className="ios-icon-squircle"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              backgroundColor: `${color}25`,
              color: color,
              border: `1px solid ${color}40`,
              boxShadow: `0 8px 20px ${color}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.25s cubic-bezier(0.2, 0.9, 0.3, 1)'
            }}
          >
            {renderCategoryIcon(icon, 28, color)}
          </div>

          <div>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', fontWeight: 700 }}>
              Live Squircle Preview
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginTop: '2px' }}>
              {name.trim() || 'Untitled Category'}
            </div>
            <div style={{ fontSize: '0.75rem', color: color, fontWeight: 600, marginTop: '2px' }}>
              Apple HIG Accent · {color}
            </div>
          </div>
        </div>

        {/* Inset Group: Category Name */}
        <div className="ios-inset-group" style={{ margin: 0 }}>
          <div style={{ padding: '14px 18px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
                color: 'var(--text-tertiary)',
                marginBottom: '6px'
              }}
            >
              Category Label
            </label>
            <input
              type="text"
              placeholder="e.g. Fuel, Client Dinner, Software Tools"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Apple HIG Color Palette Selector */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              marginBottom: '10px'
            }}
          >
            Color Palette (Apple HIG)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {APPLE_COLORS.map(item => (
              <button
                key={item.hex}
                type="button"
                onClick={() => setColor(item.hex)}
                className="ios-tap-target"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: item.hex,
                  border: color === item.hex ? '2.5px solid #ffffff' : '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: color === item.hex ? `0 0 12px ${item.hex}` : '0 2px 6px rgba(0, 0, 0, 0.2)',
                  transform: color === item.hex ? 'scale(1.15)' : 'scale(1)',
                  transition: 'all 0.18s cubic-bezier(0.2, 0.9, 0.3, 1)'
                }}
                title={item.label}
              >
                {color === item.hex && <Check size={16} color="#ffffff" strokeWidth={3} />}
              </button>
            ))}
          </div>
        </div>

        {/* Icon Picker Grid */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              marginBottom: '10px'
            }}
          >
            Select Squircle Symbol ({CATEGORY_ICONS.length} available)
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
              gap: '8px',
              maxHeight: '180px',
              overflowY: 'auto',
              padding: '6px',
              borderRadius: '16px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: 'var(--border-hairline)'
            }}
          >
            {CATEGORY_ICONS.map(item => {
              const isSelected = icon === item.name;
              const IconComp = item.icon;
              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => setIcon(item.name)}
                  className="ios-tap-target"
                  style={{
                    height: '44px',
                    borderRadius: '12px',
                    border: isSelected ? `2px solid ${color}` : '1px solid var(--border-subtle)',
                    background: isSelected ? `${color}30` : 'rgba(255, 255, 255, 0.04)',
                    color: isSelected ? color : 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}
                  title={item.label}
                >
                  <IconComp size={20} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="ios-tap-target"
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: 'var(--radius-pill)',
              border: 'var(--border-hairline)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: '0.92rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="ios-primary-button ios-tap-target"
            style={{
              flex: 2,
              padding: '14px',
              fontSize: '0.92rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? (
              <span>Saving...</span>
            ) : (
              <>
                <Sparkles size={16} />
                <span>{isEditing ? 'Save Changes' : 'Create Category'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </GlassModal>
  );
};
